import { COMPUTE_MODE } from '../../model/enums';
import { ComputeResult, IBaselineValue, SisterResult } from '../../model/interfaces';
import { FeiVO } from '../../vo/workingdata/fei.vo';
import { WidgetCell, WidgetLine } from '../../vo/workingdata/widget-line.obj';
import { FormulaCoreEngine } from '../computer/formula.core.engine';
import { MeasModeItem } from '../computer/measure.mode.obj';

/**
 * retour de boucle de looper / recompute
 * contient 
 * le FEI cible
 * la pile des éléments recalculés
 * la pile des éléments supprimés
 * la pile des éléments créés
 */
export interface LoopResult {
  fei: FeiVO;
  created: Set<WidgetLine>;
  deleted: Set<WidgetLine>;
  updated: Map<WidgetLine, Set<WidgetCell>>;
}

/**
 * Objet de travail pour le recalcul
 * - contient le LoopResult
 * - porte la map des pendings (level => scen => set<widgetCell>)
 */
export interface LoopVO {
  loopResults: Map<FeiVO, LoopResult>; // la map de result par FEI
  pendings: Map<number, Map<string, Set<WidgetCell>>>; // la map de pendings pour recalculs
  trace?: boolean; // activer les traces ??
  computeMode?:boolean; // mode calcul compute (back). On va supprimer les collections sur un recompute 
}


/**
 * Moteur de recalcul
 * tour de boucle sur les pendings pour jouer ce qu'on peut...
 */
export class DataLooper {

  /**
   * construit le loopResult de la map si nécessaire, et le retourne
   * 
   * @param loopResults 
   * @param fei 
   * @returns 
   */
  public static getLooperResult(loopResults: Map<FeiVO, LoopResult>, fei: FeiVO): LoopResult {
    if (!loopResults.has(fei)) {
      loopResults.set(fei, { fei, updated: new Map<WidgetLine, Set<WidgetCell>>(), created: new Set<WidgetLine>(), deleted: new Set<WidgetLine>() });
    }
    return loopResults.get(fei);
  }


  /**
   * recalcul pour un FEI
   * @param fei à l'origine du recalcul. Utilisé uniquement pour l'accès au modèle
   * @param loopResults la map de résultat en cours de construction
   * @param trackUpdate pour détection en cascade des mesures modifiées et collecte des triggers
   * @returns rien, mais alimente la map
   */
  public static doLoop(fei: FeiVO, loopResults: Map<FeiVO, LoopResult>, trackUpdate?: Map<string, [string, string]>): void {

    // l'attente de calcul existante pour ce fei
    // par scenario cible => clé de BL = measureCode = AkmCell
    const penMap: Map<number, Map<string, Map<string, WidgetCell>>> = fei.model.cellMatrixEngine.getFeiPendingMap(fei.visionStepLevel, fei.scenario);
    // console.log('doLoop', loopResults, penMap);
    let dependencies: Set<WidgetCell> = new Set(); // les dépendances identifiées sur le play des pendings


    // calcul des mesures en attente dans la map de pendings
    if (!!penMap) {
      [...penMap.keys()].forEach((level: number) => { // pour chaque step requis

        [...penMap.get(level).keys()].forEach((targetBlKey: string) => { // pour chaque WidgetLine
          [...penMap.get(level).get(targetBlKey).values()].forEach((cell: WidgetCell) => { // pour chaque cellule
            dependencies.add(cell);
          });
        });
      });

      // un tour de boucle sur les dépendances
      this.subLoop(fei, loopResults, dependencies, trackUpdate);
    }
  }

  /**
   * un tour de boucle du looper
   * @param fei, le fei à l'origine de la demande de recalcul
   * @param loopResults: Map<FeiVO, LoopResult> le résultat consolidé du looper
   * @param dependencies les dépendances en cours de résolution
   * @param trackUpdate pour détection en cascade des mesures modifiées et collecte des triggers
   */
  private static subLoop(fei: FeiVO, loopResults: Map<FeiVO, LoopResult>, dependencies: Set<WidgetCell>, trackUpdate?: Map<string, [string, string]>): void {

    let newDependencies: Set<WidgetCell> = new Set(); // les dépendances identifiées pendant qu'on joue... les dépendances

    let targetFEI: FeiVO; // fei nécessaire pour le calcul
    let targetLine: WidgetLine; // la baseline à recalculer
    let cr: ComputeResult; // résultat du calcul
    let currentLoopResult: LoopResult; //résultat actuel du looper (pour le targetFEI)
    let mmi: MeasModeItem; // l'expression pour le calcul

    // map des series déjà reconstruites pour ne pas boucler infini
    // par measureFullKey, sisterKey[]
    let sisterResultPlayed: Map<string, Set<string>> = new Map();

    while (dependencies.size > 0) {
      [...dependencies.values()].forEach((cell: WidgetCell) => {

        targetFEI = fei.model.dataEngine.getFei(COMPUTE_MODE.LOCAL, cell.l, cell.sc);

        if (!!targetFEI) {
          // ajout de l'entrée de looper si inexistante dans la map de résult
          currentLoopResult = this.getLooperResult(loopResults, targetFEI);

          // targetLine = targetFEI.getBaselinesForKey(scenario, cell.key); // la baseline cible
          targetLine = targetFEI.getWidgetLineForKey(cell.sc, cell.k); // la baseline cible
        } else {
          targetLine = null;
        }

        if (!!targetLine) { // si la baseline n'a pas été trouvée dans le fei, on s'arrête là

          if (!currentLoopResult.updated.has(targetLine)) { // ajout de l'entrée de ligne dans le résultat du looper
            currentLoopResult.updated.set(targetLine, new Set());

            // suppression de sa clé de texte (filtre générique)
            // console.log('datalooper delete', targetLine.byWidgetStringMap);
            // targetLine.byWidgetStringMap = null;

          }
          // on cherche une mesure
          mmi = targetFEI.matrix.get(cell.m)?.mmi;


          if (mmi) {
            cr = FormulaCoreEngine.computeMmiDependencies(mmi, targetLine, targetFEI); // recalul du mm
            // cr = FormulaCoreEngine.computeLine(cell.mkey, targetLine, targetFEI); // recalul du mm
            // ce qu'on met à jour, c'est la matrice, donc les BaselineValue
            let blv: IBaselineValue = targetFEI.model.cellMatrixEngine.getCell(cell.l, cell.k, cell.sc, cell.m);
            // TODO TODO marche pas sur subGrid de form if(blv.valor !== cr.valor) {
            
            if(!!trackUpdate && cr?.valor != blv.v) {
              // console.log('datalooper track', mmi.mm.code);
              trackUpdate.set(mmi.mm.code, [blv.v, cr?.valor]);
            }
            
            blv.v = cr.valor; // on met à jour la vraie cellule

            if(targetFEI.translatableEntries?.has(cell.m)) { // mémorisation des libellés traduits si nécessaire
              // ici, c'est la première génèse
              if(!blv.vlo) {
                blv.vlo = {};
              }
              blv.vlo[fei.model.currentLocaleCode] = cr.valor;
              // console.log('trsValue looper', blv);
            }



            currentLoopResult.updated.get(targetLine).add(cell); // ajout de la cellule modifiée dans le looper
            if (blv.w?.size > 0) {
              blv.w.forEach((childCell:WidgetCell) => {
                fei.model.cellMatrixEngine.clearDexieMatrixCell(childCell); // suppression de la valeur mémorisée dexie
                newDependencies.add(childCell);
              });
            }
            // } else {
            //   console.log('looper sameValue', cell, cell.mkey, blv.valor);
            // }

            // en cas de series sur une sous-value
            // on doit tester en dehors du contrôle d'égalité, car c'est peut-être une cellule source de calcul qui a été modifiée
            if (blv.si?.size > 0) { 
              
              blv.si.forEach((sr: SisterResult) => {
                if (!sisterResultPlayed.get(sr.magg)?.has(sr.sk)) {

                  // if(sr.magg === '@@SER|CHRONO#2@CHRONO#3|VTE_TY_CANET_XSEM#I') {
                  //   console.log('series on looper', blv.k, blv.m, sr.magg);
                  // }

                  [...sr.sis.values()].forEach(wc => {
                    newDependencies.add(wc);
                  });
                  fei.model.cellMatrixEngine.killSerie(sr.l, sr.sc, sr.magg, sr.sk);
                  if (!sisterResultPlayed.has(sr.magg)) {
                    sisterResultPlayed.set(sr.magg, new Set());
                  }
                  sisterResultPlayed.get(sr.magg).add(sr.sk);
                } else {
                  // blv.si.delete(sr);
                }
              });
            }

          } else {
            // console.log('missing measure on looper', cell)
          }



        }

      });
      // fin du tour de boucle
      dependencies = new Set([...newDependencies]); // tout ce qui est déjà calculé
      newDependencies = new Set();
    }
  }
}
